import React, { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import "../style/userProfile/userProfile.css";
import { useParams, useHistory } from "react-router-dom";
import axios from "../config/axios";
import { useTranslation } from 'react-i18next';

function UserProfile() {
    const { t } = useTranslation();
    const { userId } = useParams();
    const history = useHistory();
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [userPosts, setUserPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("home");
    const [isFollowed, setIsFollowed] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);
    const [followerCount, setFollowerCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);

    const currentUser = JSON.parse(localStorage.getItem('user'));
    const isOwnProfile = currentUser?.id === userId;

    useEffect(() => {
        if (!userId) return;

        setLoading(true);

        // Fetch user data, profile, posts, and follow counts in parallel
        Promise.all([
            axios.get(`/user/${userId}`),
            axios.get(`/user/${userId}/profile`).catch(() => ({ data: { profile: null } })),
            axios.get(`/user/${userId}/post`).catch(() => ({ data: { posts: [] } })),
            axios.get(`/follow/${userId}/followers`).catch(() => ({ data: [] })),
            axios.get(`/follow/${userId}/following`).catch(() => ({ data: [] }))
        ])
            .then(([userRes, profileRes, postsRes, followersRes, followingRes]) => {
                setUser(userRes.data);
                setProfile(profileRes.data?.profile || null);
                setUserPosts(postsRes.data?.posts || []);
                setFollowerCount(followersRes.data?.length || 0);
                setFollowingCount(followingRes.data?.length || 0);
            })
            .catch(err => {
                console.error('Error fetching user profile:', err);
            })
            .finally(() => setLoading(false));

        // Check if current user follows this user
        if (currentUser?.id && currentUser.id !== userId) {
            axios.get(`/follow/${currentUser.id}/following`)
                .then(response => {
                    const data = Array.isArray(response.data) ? response.data : [];
                    const followedIds = data.map(f => f.followed_id);
                    setIsFollowed(followedIds.includes(userId));
                })
                .catch(err => console.error('Error checking follow status:', err));
        }
    }, [userId]);

    const handleFollow = async () => {
        if (!currentUser) {
            history.push('/login');
            return;
        }

        if (isOwnProfile) return;

        setFollowLoading(true);

        try {
            if (isFollowed) {
                await axios.delete(`/follow/${userId}`);
                setIsFollowed(false);
                setFollowerCount(prev => prev - 1);
            } else {
                await axios.post(`/follow/${userId}`, {});
                setIsFollowed(true);
                setFollowerCount(prev => prev + 1);
            }
        } catch (err) {
            console.error('Error following/unfollowing:', err);
        } finally {
            setFollowLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="userProfile-container">
                <Typography variant="h5">Loading profile...</Typography>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="userProfile-container">
                <Typography variant="h5">User not found</Typography>
            </div>
        );
    }

    const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
    const profilePhoto = profile?.photo || null;

    return (
        <div className="userProfile-container">
            <style>
                {`
                    .userProfile-left-subbar-item {
                        padding: 10px 20px;
                        cursor: pointer;
                        transition: background-color 0.3s ease-in-out;
                    }
                    .userProfile-left-subbar-item:hover {
                        background-color: rgba(0, 0, 0, 0.1);
                        border-radius: 5px;
                    }
                    .button.active {
                        background-color: #007bff;
                        color: white;
                    }
                    .follow-button {
                        padding: 10px 24px;
                        border: none;
                        border-radius: 20px;
                        cursor: pointer;
                        font-weight: bold;
                        transition: all 0.3s ease;
                        margin-top: 15px;
                    }
                    .follow-button.following {
                        background-color: #e0e0e0;
                        color: #333;
                    }
                    .follow-button.not-following {
                        background-color: #6750A4;
                        color: white;
                    }
                    .follow-button:hover {
                        opacity: 0.9;
                    }
                    .follow-button:disabled {
                        cursor: wait;
                        opacity: 0.7;
                    }
                    .stats-container {
                        display: flex;
                        gap: 20px;
                        margin-top: 15px;
                        justify-content: center;
                    }
                    .stat-item {
                        text-align: center;
                    }
                    .stat-number {
                        font-weight: bold;
                        font-size: 18px;
                    }
                    .stat-label {
                        color: #666;
                        font-size: 14px;
                    }
                    .post-item {
                        padding: 15px;
                        background-color: #f9f9f9;
                        border-radius: 8px;
                        margin-bottom: 12px;
                        cursor: pointer;
                        transition: background-color 0.2s;
                    }
                    .post-item:hover {
                        background-color: #f0f0f0;
                    }
                    .userProfile-right-image {
                        width: 150px;
                        height: 150px;
                        border-radius: 50%;
                        object-fit: cover;
                        border: 2px solid #ccc;
                        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    }
                `}
            </style>
            <div className="userProfile-left">
                <Typography variant="h4" className="userProfile-title">
                    {userName}
                </Typography>
                <div className="userProfile-left-subbar">
                    <Typography
                        variant="h6"
                        className={`userProfile-left-subbar-item button ${activeTab === "home" ? "active" : ""}`}
                        onClick={() => setActiveTab("home")}
                    >
                        {t('UserProfilePage.Posts')}
                    </Typography>
                    <Typography
                        variant="h6"
                        className={`userProfile-left-subbar-item button ${activeTab === "about" ? "active" : ""}`}
                        onClick={() => setActiveTab("about")}
                    >
                        {t('UserProfilePage.About')}
                    </Typography>
                </div>
                <hr className="userProfile-left-hr" />
                <div className="userProfile-left-content">
                    {activeTab === "home" ? (
                        <div>
                            <Typography variant="h6" style={{ marginBottom: '15px' }}>
                                {t('UserProfilePage.PostsBy', { userName, postCount: userPosts.length })}
                            </Typography>
                            {userPosts.length === 0 ? (
                                <Typography variant="body2" style={{ color: '#666' }}>
                                    This user hasn't published any posts yet.
                                </Typography>
                            ) : (
                                userPosts.map((post, index) => (
                                    <div
                                        key={post.id || index}
                                        className="post-item"
                                        onClick={() => history.push('/')}
                                    >
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            {post.title}
                                        </Typography>
                                        {post.subtitle && (
                                            <Typography variant="body2" style={{ color: '#666', marginTop: '5px' }}>
                                                {post.subtitle}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" style={{ color: '#999', marginTop: '5px', display: 'block' }}>
                                            {new Date(post.created).toLocaleDateString()}
                                        </Typography>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        <div className="userProfile-left-bio">
                            <Typography variant="body1">
                                {profile?.biography || `${userName} is a contributor to this blog.`}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
            <div className="userProfile-right">
                <div className="userProfile-right-image-wrapper" style={{ textAlign: 'center' }}>
                    <img
                        src={profilePhoto || "/blogger.jpg"}
                        alt={userName}
                        className="userProfile-right-image"
                        onError={(e) => { e.target.src = "/blogger.jpg"; }}
                    />
                    <Typography variant="h6" style={{ marginTop: '12px', fontWeight: 500 }}>
                        {userName}
                    </Typography>

                    <div className="stats-container">
                        <div className="stat-item">
                            <div className="stat-number">{followerCount}</div>
                            <div className="stat-label">{t('UserProfilePage.Followers')}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">{followingCount}</div>
                            <div className="stat-label">{t('UserProfilePage.Following')}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">{userPosts.length}</div>
                            <div className="stat-label">{t('UserProfilePage.Posts')}</div>
                        </div>
                    </div>

                    {!isOwnProfile && currentUser && (
                        <button
                            className={`follow-button ${isFollowed ? 'following' : 'not-following'}`}
                            onClick={handleFollow}
                            disabled={followLoading}
                        >
                            {followLoading ? '...' : (isFollowed ? t('UserProfilePage.Following') : t('UserProfilePage.Follow'))}
                        </button>
                    )}

                    {isOwnProfile && (
                        <button
                            className="follow-button not-following"
                            onClick={() => history.push('/settings')}
                            style={{ marginTop: '15px' }}
                        >
                            {t('UserProfilePage.EditProfile')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserProfile;
